import React, { useCallback, useEffect, useState, useTransition } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { debounce } from 'lodash'
import { addressSchema } from '../../validation/addressValidation'
import NxTextInput from 'components/inputs/NxTextInput'
import NxSelectInput from 'components/inputs/NxSelectInput'
import { useAddressStore } from 'components/addressBlock'
import { DropdownContainer, DropdownContent } from 'components/dropdown'
import { z } from 'zod'
import { STATES } from 'modules/clinicalScheduling/utils/constants'
import { fetchAutoCompleteAddresses } from 'components/addressBlock/components/utils'
import { SmartyAddressAutocomplete } from '../../types/address'
import { usePaymentStore } from 'stores/paymentStore'
import SfCheckbox from 'components/overrides/SfCheckbox'

// Use the inferred type from the schema to ensure perfect match
type BillingAddressFormValues = z.infer<typeof addressSchema>

const BillingAddress: React.FC = () => {
  const {
    billingAddressSameAsShipping,
    billingAddress,
    setBillingAddress,
    setBillingAddressSameAsShipping,
    setBillingAddressValid
  } = usePaymentStore()

  // Use the main address store for shipping information
  const {
    addressWebConfirmed,
    addressPatientConfirmed
  } = useAddressStore()

  const [ showErrors, setShowErrors ] = useState<boolean>( false )
  const [ suggestions, setSuggestions ] = useState<SmartyAddressAutocomplete[]>( [] )
  const [ isPending, startTransition ] = useTransition()
  const [ isAddressDropdownOpen, setIsAddressDropdownOpen ] = useState( false )

  const { register, formState, setValue } = useForm<BillingAddressFormValues>({
    resolver: zodResolver( addressSchema ),
    shouldFocusError: true,
    mode: `onChange`
  })

  useEffect( () => {
    setBillingAddress({
      firstName: ``,
      lastName: ``,
      street: ``,
      street2: ``,
      city: ``,
      state: ``,
      zipCode: ``
    })
    setBillingAddressSameAsShipping( true )
    setBillingAddressValid( true )
  }, [] )

  const { errors } = formState

  const onBlur = () => {
    setShowErrors( true )
  }

  // Address suggestion fetching function
  const fetchAddressSuggestions = ( addressValue: string ) => {
    startTransition( () => {
      if ( addressValue?.length >= 3 ) {
        fetchAutoCompleteAddresses( addressValue )
          .then( response => {
            const addressSuggestions = response?.suggestions || []
            setSuggestions( addressSuggestions )
            setIsAddressDropdownOpen( Boolean( addressSuggestions?.length ) )
          })
          .catch( error => {
            console.error( `Error fetching address suggestions:`, error )
            setSuggestions( [] )
            setIsAddressDropdownOpen( false )
          })
      } else {
        setSuggestions( [] )
        setIsAddressDropdownOpen( false )
      }
    })
  }

  // Debounce the suggestion function to avoid too many API calls
  const debouncedSuggestionFunction = useCallback(
    debounce( fetchAddressSuggestions, 300 ),
    []
  )

  // Handle selection of an address suggestion
  const handlePlaceClick = ( suggestion: SmartyAddressAutocomplete ) => {
    const updatedBilling = {
      ...billingAddress,
      street: suggestion.street_line,
      street2: suggestion.secondary || ``,
      city: suggestion.city,
      state: suggestion.state,
      zipCode: suggestion.zipcode
    }

    setBillingAddress( updatedBilling )

    // Update form values
    setValue( `street`, suggestion.street_line )
    setValue( `street2`, suggestion.secondary || `` )
    setValue( `city`, suggestion.city )
    setValue( `state`, suggestion.state )
    setValue( `zipCode`, suggestion.zipcode )

    const valid = addressSchema.safeParse( updatedBilling ).success
    setBillingAddressValid( valid )
    setIsAddressDropdownOpen( false )
  }

  // Handle text input in the street field
  const handleTypeAheadChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    setShowErrors( false )
    const streetValue = e.target.value
    setBillingAddress({
      ...billingAddress,
      street: streetValue
    })
    setValue( `street`, streetValue )
    debouncedSuggestionFunction( streetValue )
  }

  const handleTypeAheadBlur = ( e: React.FocusEvent<HTMLDivElement> ) => {
    // Hide predictions if user clicks outside the dropdown
    if (
      e.relatedTarget === null ||
      !e.currentTarget.contains( e.relatedTarget as Node )
    ) {
      setIsAddressDropdownOpen( false )
    }
  }

  const handleStreet2Focus = () => {
    setIsAddressDropdownOpen( false )
  }

  const onChange = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    setShowErrors( false )
    const updatedBilling = {
      ...billingAddress,
      [e.target.name]: e.target.value || ``
    }
    setBillingAddress({
      ...updatedBilling
    })
    const valid = addressSchema.safeParse( updatedBilling ).success
    setBillingAddressValid( valid )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="font-light">
        {`Billing Address`}
        <span className="text-error ml-1">{`*`}</span>
      </p>
      <div
        className="flex items-center justify-start gap-3 cursor-pointer"
        onClick={() => {
          const newValue = !billingAddressSameAsShipping
          setBillingAddressSameAsShipping( newValue )
          // If toggling to use same as shipping, we can use the shipping address verification status
          if ( newValue ) {
            setBillingAddressValid( addressWebConfirmed || addressPatientConfirmed )
          }
        }}
        data-testid="snl-address-same"
      >
        <SfCheckbox
          id="billing-same-as-shipping"
          checked={billingAddressSameAsShipping}
          className="mt-1 min-w-[25px] h-[25px]"
        />
        <p className="font-light">{`Same as shipping address`}</p>
      </div>
      {!billingAddressSameAsShipping && (
        <div className="flex flex-col gap-3">
          <NxTextInput
            label="First Name"
            data-testid="address_firstname"
            type="text"
            id="firstName"
            name="firstName"
            required
            value={billingAddress?.firstName}
            register={register}
            onChange={onChange}
            onBlur={onBlur}
            invalid={showErrors && Boolean( errors?.firstName?.message )}
            error={showErrors && errors?.firstName?.message ? String( errors.firstName.message ) : ``}
          />
          <NxTextInput
            label="Last Name"
            data-testid="address_lastname"
            type="text"
            id="lastName"
            name="lastName"
            required
            value={billingAddress?.lastName}
            register={register}
            onChange={onChange}
            onBlur={onBlur}
            invalid={showErrors && Boolean( errors?.lastName?.message )}
            error={showErrors && errors?.lastName?.message ? String( errors.lastName.message ) : ``}
          />
          {/* Address with autocomplete */}
          <DropdownContainer onBlur={handleTypeAheadBlur}>
            <NxTextInput
              label="Street"
              data-testid="address_street"
              placeholder="Enter a location"
              type="text"
              id="street"
              name="street"
              required
              value={billingAddress?.street}
              register={register}
              onChange={handleTypeAheadChange}
              onBlur={onBlur}
              invalid={showErrors && Boolean( errors?.street?.message )}
              error={showErrors && errors?.street?.message ? String( errors.street.message ) : ``}
            />
            <DropdownContent
              shouldShow={isAddressDropdownOpen}
              isLoading={isPending}
            >
              <ul>
                {suggestions?.map( ( suggestion, index ) => (
                  <li
                    id="place-prediction"
                    tabIndex={0}
                    key={index}
                    className="cursor-pointer p-3 hover:bg-secondaryBlue-tint3"
                    onClick={() => handlePlaceClick( suggestion )}
                    data-testid="snl-place-suggestion"
                  >
                    {`${suggestion.street_line}${
                      suggestion?.secondary ? `, ${suggestion?.secondary}` : ``
                    }, ${suggestion?.city}, ${suggestion?.state} ${
                      suggestion?.zipcode
                    }`}
                  </li>
                ) )}
              </ul>
            </DropdownContent>
          </DropdownContainer>
          <NxTextInput
            label="Street 2"
            type="text"
            id="street2"
            name="street2"
            value={billingAddress?.street2}
            data-testid="address_street2"
            register={register}
            onFocus={handleStreet2Focus}
            onChange={onChange}
            onBlur={onBlur}
            invalid={showErrors && Boolean( errors?.street2?.message )}
            error={showErrors && errors?.street2?.message ? String( errors.street2.message ) : ``}
          />
          <NxTextInput
            label="City"
            type="text"
            id="city"
            name="city"
            required
            data-testid="address_city"
            value={billingAddress?.city}
            onChange={onChange}
            register={register}
            invalid={Boolean( errors?.city?.message )}
            error={errors?.city?.message ? String( errors.city.message ) : undefined}
          />
          <NxSelectInput
            label="State"
            id="state"
            name="state"
            required
            value={billingAddress?.state}
            register={register}
            onChange={( e ) => {
              setBillingAddress({
                ...billingAddress,
                state: e.target.value
              })
            }}
            data-testid="address_state"
            size="base"
            placeholder="Please select a State"
            invalid={Boolean( errors?.state?.message )}
            error={errors?.state?.message ? String( errors.state.message ) : undefined}
          >
            {STATES.map( state => (
              <option key={state.value} value={state.value}>
                {state.name}
              </option>
            ) )}
          </NxSelectInput>
          <NxTextInput
            label="Zip"
            type="text"
            id="zipCode"
            name="zipCode"
            required
            value={billingAddress?.zipCode}
            onChange={onChange}
            register={register}
            pattern="[0-9]*"
            invalid={showErrors && Boolean( errors?.zipCode?.message )}
            data-testid="address_zip"
            maxLength={5}
            error={showErrors && errors?.zipCode?.message ? String( errors.zipCode.message ) : ``}
          />
        </div>
      )}
    </div>
  )
}

export default BillingAddress